import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Waves, MoonStar, Quote } from "lucide-react";
import PortraitSlot from "../components/PortraitSlot";
import { Seo } from "../components/Layout";
import BookingButton from "../components/BookingButton";
import TestimonialCard, { Stars } from "../components/TestimonialCard";
import { TESTIMONIALS, AVERAGE_RATING, REVIEW_COUNT } from "../data/testimonials";
import api from "../lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1761027621746-8ecd3ba01334?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80";
const SERVICES = [
  {
    icon: Waves,
    title: "Hypnose ericksonienne",
    desc: "Une méthode douce et reconnue pour agir sur le stress, l'anxiété, le sommeil, la confiance en soi, les blocages émotionnels et l'accompagnement d'un changement de vie.",
    price: "80 € · env. 1h à 1h15",
    badge: "Ouverture des séances en septembre 2026",
    img: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
  },
  {
    icon: MoonStar,
    title: "Hypnose régressive et spirituelle",
    desc: "Un espace d'exploration plus profond, pour les personnes en quête de sens. Un travail d'ouverture, de discernement et de reconnexion à soi, qui invite à comprendre ce qui se rejoue en profondeur et à retrouver un chemin plus juste.",
    price: "120 € · env. 2h",
    img: "https://images.unsplash.com/photo-1774242077294-a3df41345464?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
  },
];

export default function Home() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    document.title = "Parfaits Imparfaits – Valentine DL, Hypnose ericksonienne à Pantin";
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <>
      <Seo canonical="https://www.parfaitsimparfaits.fr/" url="https://www.parfaitsimparfaits.fr/" />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Parfaits Imparfaits — Valentine DL",
            description:
              "Valentine DL, praticienne en hypnose régressive et spirituelle à Pantin (Métro Église de Pantin, ligne 5, 15 min de Paris) et en ligne (visio). Ouverture de l'hypnose ericksonienne à partir de septembre 2026.",
            email: "v.parfaitsimparfaits@gmail.com",
            url: "https://www.parfaitsimparfaits.fr",
            address: { "@type": "PostalAddress", addressLocality: "Pantin", addressCountry: "FR" },
            areaServed: ["Pantin", "Paris", "Île-de-France", "France"],
            sameAs: [
              "https://www.instagram.com/parfaits_imparfaits/",
              "https://www.youtube.com/@ParfaitsImparfaits",
            ],
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: AVERAGE_RATING,
              bestRating: 5,
              worstRating: 1,
              reviewCount: REVIEW_COUNT,
            },
            review: TESTIMONIALS.slice(0, 5).map((t) => ({
              "@type": "Review",
              author: { "@type": "Person", name: t.name },
              reviewRating: { "@type": "Rating", ratingValue: t.rating, bestRating: 5 },
              reviewBody: t.text,
            })),
          }),
        }}
      />

      {/* ---------------- HERO ---------------- */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden grain" data-testid="hero-section">
        <img
          src={HERO_BG}
          alt="Lumière douce traversant un paysage paisible — Parfaits Imparfaits, hypnose spirituelle"
          loading="eager"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sand-100/40 via-sand-100/60 to-sand-100/95" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 pt-32 pb-24 w-full">
          <div className="max-w-3xl animate-fade-up">
            <p className="eyebrow mb-6">Hypnose à Pantin · Séances en ligne</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif font-light leading-[1.18] tracking-tight text-sand-900 [text-shadow:0_1px_24px_rgba(255,255,255,0.6)]">
              Hypnose à Pantin,<br />
              <em className="italic font-normal text-[#B07A45] inline-block mt-2">à 15 min de Paris.</em>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-sand-700 leading-relaxed max-w-2xl">
              Valentine DL, praticienne en <strong className="text-sand-900 font-medium">hypnose régressive</strong> et
              <strong className="text-sand-900 font-medium"> spirituelle</strong>.
              Séances au cabinet à Pantin (Métro Église de Pantin, ligne 5) et en visio.
              <span className="block mt-3 text-sand-600 text-base italic">
                L'hypnose ericksonienne sera proposée à partir de septembre 2026.
              </span>
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <BookingButton testid="hero-cta-rdv">Réserver une séance</BookingButton>
              <Link to="/hypnose" data-testid="hero-cta-services" className="btn-outline">
                Découvrir l'hypnose
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- INTRO ---------------- */}
      <section className="py-24 lg:py-32" data-testid="intro-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-4 md:col-start-1">
            <PortraitSlot />
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="eyebrow mb-5">Bienvenue</p>
            <h2 className="text-4xl sm:text-5xl font-serif font-light leading-tight text-sand-900">
              Une présence pour accompagner votre <em className="text-sand-500">transformation intérieure</em>.
            </h2>
            <div className="mt-8 space-y-5 text-sand-700 leading-relaxed text-lg">
              <p>
                Je suis <strong className="text-sand-900 font-medium">Valentine DL</strong>,
                praticienne en <strong className="text-sand-900 font-medium">hypnose régressive et
                spirituelle</strong>. J'exerce à <strong className="text-sand-900 font-medium">Pantin (93)</strong>,
                à proximité du métro Église de Pantin — à 15 minutes de Paris — ainsi qu'en ligne,
                en visioconférence.
              </p>
              <p>
                Je poursuis actuellement mon parcours de formation en{" "}
                <strong className="text-sand-900 font-medium">hypnose ericksonienne</strong> ;
                les séances seront proposées à partir de <em>septembre 2026</em>.
              </p>
              <p>
                Mon approche se veut <strong className="text-sand-900 font-medium">sobre, contemporaine
                et humaine</strong>. Je crois profondément qu'un accompagnement de qualité ne repose
                ni sur le sensationnel ni sur les promesses, mais sur une <strong className="text-sand-900 font-medium">présence
                juste</strong> et sur le respect du rythme de chacun·e.
              </p>
              <p>
                Mon rôle&nbsp;: tenir un cadre sûr, poser les bonnes questions, guider avec précision —
                vous aider à accéder à vos propres ressources intérieures.
              </p>
            </div>
            <div className="mt-10">
              <Link to="/qui-suis-je" data-testid="intro-cta-about" className="btn-outline">
                En savoir plus sur Valentine <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- SERVICES ---------------- */}
      <section className="py-24 lg:py-32 bg-sand-200/40" data-testid="services-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <p className="eyebrow mb-5">L'accompagnement</p>
            <h2 className="text-4xl sm:text-5xl font-serif font-light leading-tight text-sand-900">
              Deux approches, une même <em className="text-sand-500">exigence de présence</em>.
            </h2>
            <p className="mt-6 text-sand-700 text-lg leading-relaxed">
              De la thérapie brève à l'exploration intérieure profonde,
              choisissons ensemble l'accompagnement le plus adapté à votre besoin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.title}
                  data-testid={`service-card-${i}`}
                  className="group relative overflow-hidden rounded-2xl bg-sand-100 border border-sand-300/60 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={s.img}
                      alt={`${s.title} — séance avec Valentine DL`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-sand-900/40 to-transparent" />
                    <div className="absolute top-4 left-4 glass rounded-full p-3">
                      <Icon size={20} className="text-sand-900" />
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="text-2xl font-serif text-sand-900">{s.title}</h3>
                    <p className="mt-3 text-sand-700 leading-relaxed text-[15px]">{s.desc}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <p className="inline-block rounded-full bg-sand-200/60 border border-sand-300/60 px-3 py-1 text-xs text-sand-800 font-medium">
                        {s.price}
                      </p>
                      {s.badge && (
                        <p
                          data-testid={`service-badge-${i}`}
                          className="inline-block rounded-full bg-[#B07A45]/10 border border-[#B07A45]/30 px-3 py-1 text-xs text-[#8A5F32] font-medium italic"
                        >
                          {s.badge}
                        </p>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="mt-14 flex justify-center">
            <BookingButton testid="services-cta-contact">Réserver une séance</BookingButton>
          </div>
        </div>
      </section>

      {/* ---------------- QUOTE / SHIFT MESSAGE ---------------- */}
      <section className="py-24 lg:py-32" data-testid="quote-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Quote size={36} className="mx-auto text-sand-500 mb-6" />
          <p className="text-3xl sm:text-4xl font-serif italic font-light leading-[1.3] text-sand-900">
            « L'hypnose est un état naturel et sécurisant, dans lequel votre
            esprit mobilise ses propres ressources. Mon rôle est de créer le
            cadre pour que ce mouvement puisse s'opérer. »
          </p>
          <p className="mt-8 eyebrow text-sand-600">Valentine DL</p>
        </div>
      </section>

      {/* ---------------- TESTIMONIALS ---------------- */}
      <section className="py-24 lg:py-32 bg-sand-900 text-sand-100" data-testid="testimonial-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="eyebrow !text-sand-500 mb-4">Vos retours</p>
            <h2 className="text-4xl sm:text-5xl font-serif font-light leading-tight">
              Ce qu'elles & ils <em className="text-[#C29A73]">en disent</em>
            </h2>
            <div className="mt-7 flex items-center justify-center gap-4 flex-wrap">
              <Stars count={5} />
              <p className="text-sand-300 text-sm">
                <strong className="text-white font-medium">{AVERAGE_RATING.toFixed(1)} / 5</strong> · {REVIEW_COUNT} avis Google
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 3).map((t, i) => (
              <TestimonialCard key={i} {...t} dark testid={`home-testimonial-${i}`} />
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/temoignages" data-testid="testimonial-all-link" className="btn-outline !border-sand-500 !text-sand-100 hover:!bg-sand-100 hover:!text-sand-900">
              Lire tous les témoignages <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------- BLOG TEASER ---------------- */}
      {posts.length > 0 && (
        <section className="py-24 lg:py-32" data-testid="blog-teaser-section">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
              <div className="max-w-2xl">
                <p className="eyebrow mb-4">Le blog</p>
                <h2 className="text-4xl sm:text-5xl font-serif font-light text-sand-900">
                  Ressources et <em className="text-sand-500">réflexions</em>
                </h2>
              </div>
              <Link to="/blog" data-testid="blog-teaser-all" className="btn-outline">
                Tous les articles <ArrowRight size={18} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {posts.map((p, i) => (
                <Link
                  to={`/blog/${p.slug}`}
                  key={p.id}
                  data-testid={`blog-teaser-${i}`}
                  className="group block rounded-2xl overflow-hidden bg-sand-100 border border-sand-300/60 transition-all hover:-translate-y-1 hover:shadow-xl"
                >
                  {p.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-serif text-sand-900 leading-snug">{p.title}</h3>
                    <p className="mt-3 text-sm text-sand-700 line-clamp-3">{p.excerpt}</p>
                    <span className="inline-flex items-center gap-2 mt-5 text-sand-600 text-sm group-hover:text-sand-900 transition-colors">
                      Lire l'article <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------------- CTA FINAL ---------------- */}
      <section className="py-24 lg:py-32" data-testid="cta-final-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow mb-5">Et vous&nbsp;?</p>
          <h2 className="text-4xl sm:text-5xl font-serif font-light leading-tight text-sand-900">
            Prêt·e à franchir le pas&nbsp;? Je suis là pour vous <em className="text-sand-500">accompagner</em>.
          </h2>
          <p className="mt-8 text-sand-700 text-lg max-w-2xl mx-auto">
            Séances individuelles à Pantin (Métro Église de Pantin, ligne 5,
            15 min de Paris) et séances en ligne, en visio. Réponse
            sous 24 h par e-mail.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <BookingButton testid="final-cta-rdv">Réserver une séance</BookingButton>
            <Link to="/contact" data-testid="final-cta-contact" className="btn-outline">
              Écrire un message
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
